/**
 * Utility to fetch media for stay experiences
 */

const BASE_URL = process.env.NEXT_PUBLIC_API_URL;

export interface MediaItem {
  _id: string;
  url: string;
  thumbnailUrl?: string;
  format: string; // 'image' | 'video'
  category?: string; // 'banner' | 'gallery' etc.
  isActive?: boolean;
  [key: string]: unknown;
}

/**
 * Fetch media for a stay experience by ID
 * @param stayExperienceId - The ID of the stay experience
 * @returns Array of media items or empty array if fetch fails
 */
export async function fetchStayExperienceMedia(
  stayExperienceId: string
): Promise<MediaItem[]> {
  if (!stayExperienceId) {
    return [];
  }

  try {
    const endpoint = `${BASE_URL}/media/v1/entity/stay-experience/${stayExperienceId}`;

    const response = await fetch(endpoint, {
      method: 'GET',
      headers: {
        accept: 'application/json',
      },
    });

    if (!response.ok) {
      console.warn(
        `Failed to fetch media for stay experience ${stayExperienceId}: ${response.status} ${response.statusText}`
      );
      return [];
    }

    const data = await response.json();

    // API response structure: { success: true, message: "Success", data: [...] }
    // Extract media array from response.data
    const media = data?.data || [];

    if (Array.isArray(media)) {
      return media.filter(
        (item: MediaItem) =>
          item.format === 'image' && item.isActive !== false && item.url
      );
    }

    return [];
  } catch (error) {
    console.warn(
      `Failed to fetch media for stay experience ${stayExperienceId}:`,
      error
    );
    return [];
  }
}

/**
 * Fetch media for a room option by ID
 * @param roomId - The ID of the room option
 * @returns Array of media items or empty array if fetch fails
 */
export async function fetchRoomMedia(roomId: string): Promise<MediaItem[]> {
  if (!roomId) {
    return [];
  }

  try {
    const endpoint = `${BASE_URL}/media/v1/entity/room-option/${roomId}`;

    const response = await fetch(endpoint, {
      method: 'GET',
      headers: {
        accept: 'application/json',
      },
    });

    if (!response.ok) {
      console.warn(
        `Failed to fetch media for room ${roomId}: ${response.status} ${response.statusText}`
      );
      return [];
    }

    const data = await response.json();
    const media = data?.data || [];

    if (Array.isArray(media)) {
      return media.filter(
        (item: MediaItem) =>
          item.format === 'image' && item.isActive !== false && item.url
      );
    }

    return [];
  } catch (error) {
    console.warn(`Failed to fetch media for room ${roomId}:`, error);
    return [];
  }
}

/**
 * Fetch media for multiple stay experiences
 * @param stayExperienceIds - Array of stay experience IDs
 * @returns Map of experience ID to media array
 */
export async function fetchMultipleStayExperienceMedia(
  stayExperienceIds: string[]
): Promise<Map<string, MediaItem[]>> {
  const results = new Map<string, MediaItem[]>();

  // Fetch in parallel with limit to avoid overwhelming the API
  const batchSize = 5;
  for (let i = 0; i < stayExperienceIds.length; i += batchSize) {
    const batch = stayExperienceIds.slice(i, i + batchSize);
    const batchPromises = batch.map(async id => {
      const media = await fetchStayExperienceMedia(id);
      return { id, media };
    });

    const batchResults = await Promise.allSettled(batchPromises);
    batchResults.forEach(result => {
      if (result.status === 'fulfilled') {
        results.set(result.value.id, result.value.media);
      }
    });

    // Small delay between batches to avoid rate limiting
    if (i + batchSize < stayExperienceIds.length) {
      await new Promise(resolve => setTimeout(resolve, 100));
    }
  }

  return results;
}
